import type {
  IBasePathAnalyticPageWithChild,
  IBasePathAnalyticPageWithParent,
} from '~/modules/path-analysis/types';
import type { OnChangeFunnelType } from '~/presentation/charts';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';

import { useFetchShopifyPageInfo } from '../hooks/useFetchShopifyPageInfo';
import { useFetchCampaignPathsV2 } from '../hooks/useFetchCampaignPathsV2';
import { usePathAnalyticsMerger } from '../hooks/usePathAnalyticsMerger';
import { usePathAnalysisFunnel } from '../hooks/usePathAnalysisFunnel';
import { useFetchPathAnalysis } from '../hooks/useFetchPathAnalysis';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { createContext, useContext, useMemo } from 'react';

export interface IPathAnalysisParams {
  campaignVariantID?: string;
  campaignVersionID?: string;
  mode: EAnalyticMode;
  currentDates: AnalyticDateFilter;
}

export interface PathAnalysisType {
  mode: EAnalyticMode;
  campaignID?: string;
  campaignGroupID?: string;
  campaignVariantID?: string;
  campaignVersionID?: string;
  isPriceCampaign?: boolean;
  pathAnalytics?: IBasePathAnalyticPageWithParent[];
  isLoadingMetric: boolean;
  pageWithMetric: IBasePathAnalyticPageWithChild | undefined;
  dynamicLevels: IBasePathAnalyticPageWithChild[];
  nextLevelPages: IBasePathAnalyticPageWithChild[];
  previousPathAnalyticsMap: Record<string, IBasePathAnalyticPageWithParent>;
  handleAddNextLevel: (childId: string) => void;
  handleChangeLevel: OnChangeFunnelType;
  getPagesByParentID: (data: {
    level?: number;
    parentID?: string;
  }) => IBasePathAnalyticPageWithChild[];
  handleDeleteLevel: (id?: string) => void;
}

export interface IPathAnalysisData {
  pathAnalytics: IBasePathAnalyticPageWithParent[];
  previousPathAnalyticsMap: Record<string, IBasePathAnalyticPageWithParent>;
}

export interface IPathAnalysisFirstLevel {
  id: string;
  locationPath: string;
  pageTitle: string;
  pageType: string;
  parentID: string;
  level: number;
  metric: {
    totalSessions: number;
  };
}

export interface PathAnalysisProviderProps {
  children: React.ReactNode;
  campaignID?: string;
  campaignGroupID?: string;
  campaignVariantID?: string;
  campaignVersionID?: string;
  isPriceCampaign?: boolean;
  mode: EAnalyticMode;
  data?: IPathAnalysisData;
  dataFirstLevels?: IPathAnalysisFirstLevel[];
}

export const PathAnalysisContext = createContext<PathAnalysisType | undefined>(undefined);

export const PathAnalysisProvider = (props: PathAnalysisProviderProps) => {
  const {
    children,
    campaignID,
    campaignGroupID,
    campaignVariantID,
    mode,
    data,
    campaignVersionID,
    isPriceCampaign,
  } = props;
  const { currentDates, compareCurrentDates: previousDates } = useMetricsTimeFilter();

  const pathAnalysisParams = {
    campaignVariantID,
    campaignVersionID,
    mode,
    currentDates,
  };
  const { firstLevelPaths, isFetching: isFetchingFirstLevelPaths } = useFetchCampaignPathsV2({
    ...pathAnalysisParams,
    isEnabled: !props.dataFirstLevels,
  });

  const pathAnalysisCacheKey = JSON.stringify(pathAnalysisParams);

  //can pass from props or use fetch first level paths
  const dataFirstLevels = props.dataFirstLevels || firstLevelPaths;

  const fetchPathAnalysis = useFetchPathAnalysis({
    currentDates,
    previousDates,
    campaignVariantID,
    campaignVersionID,
    mode,
    enabledQuery: !data,
  });

  const { previousPathAnalyticsMap: apiPreviousPathAnalyticsMap, isLoading: isLoadingMetric } =
    fetchPathAnalysis;

  const pathAnalytics = useMemo<IBasePathAnalyticPageWithParent[]>(() => {
    if (data) {
      return data.pathAnalytics;
    }

    const pageGroups = fetchPathAnalysis.data?.flatMap((g) =>
      g?.pathGroups?.flatMap((g) => g?.paths),
    );
    return (
      pageGroups?.filter((page): page is IBasePathAnalyticPageWithParent => page !== undefined) ||
      []
    );
  }, [data, fetchPathAnalysis.data]);

  const previousPathAnalyticsMap = data?.previousPathAnalyticsMap || apiPreviousPathAnalyticsMap;

  const { processedPathAnalytics } = usePathAnalyticsMerger({ pathAnalytics, dataFirstLevels });
  const { pathAnalyticsWithPageInfo } = useFetchShopifyPageInfo({
    pathAnalytics: processedPathAnalytics,
  });

  const pathAnalysisFunnelData = usePathAnalysisFunnel({
    pathAnalytics: pathAnalyticsWithPageInfo,
    pathAnalysisCacheKey,
    dataFirstLevels,
  });

  const isLoading = data ? false : isLoadingMetric || isFetchingFirstLevelPaths;

  return (
    <PathAnalysisContext.Provider
      value={{
        mode,
        campaignID,
        campaignGroupID,
        campaignVariantID,
        campaignVersionID,
        isPriceCampaign,
        pathAnalytics: pathAnalyticsWithPageInfo,
        previousPathAnalyticsMap: previousPathAnalyticsMap,
        isLoadingMetric: isLoading,
        ...pathAnalysisFunnelData,
      }}
    >
      {children}
    </PathAnalysisContext.Provider>
  );
};

export const usePathAnalysisContext = () => {
  const context = useContext(PathAnalysisContext);
  if (!context) {
    throw new Error('usePathAnalysisContext must be used within a PathAnalysisProvider');
  }
  return context;
};
